export const CATEGORY_STYLE = {
  critical: "bg-red-50 text-red-600 border-red-200",
  spam: "bg-orange-50 text-orange-600 border-orange-200",
  abuse: "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200",
  rival_criticism: "bg-sky-50 text-sky-600 border-sky-200",
  neutral: "bg-slate-100 text-slate-500 border-slate-200",
};

export const STATUS_STYLE = {
  needs_review: "bg-yellow-50 text-yellow-700 border-yellow-200",
  auto_hidden: "bg-red-50 text-red-600 border-red-200",
  hidden: "bg-red-50 text-red-600 border-red-200",
  deleted: "bg-slate-200 text-slate-700 border-slate-300",
  approved: "bg-green-50 text-green-600 border-green-200",
  kept: "bg-green-50 text-green-600 border-green-200",
  pending: "bg-slate-100 text-slate-500 border-slate-200",
};

const STATUS_LABEL = { auto_hidden: "auto-hidden", needs_review: "needs review" };

export function CategoryBadge({ category }) {
  return (
    <span className={`whitespace-nowrap text-[11px] font-medium capitalize px-2 py-0.5 rounded border ${CATEGORY_STYLE[category] || CATEGORY_STYLE.neutral}`}>
      {(category || "neutral").replace(/_/g, " ")}
    </span>
  );
}

export function StatusBadge({ status }) {
  return (
    <span className={`whitespace-nowrap text-[11px] font-medium capitalize px-2 py-0.5 rounded border ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
      {STATUS_LABEL[status] || (status || "").replace(/_/g, " ")}
    </span>
  );
}

// Shown when auto-moderation tried to remove a comment and the platform refused.
export function ActionErrorBadge({ error }) {
  if (!error) return null;
  return (
    <span className="text-[11px] font-medium px-2 py-0.5 rounded border bg-transparent text-red-600 border-red-400" title="The comment is still visible on the platform.">
      {error}
    </span>
  );
}

export function LangBadge({ language }) {
  return (
    <span className="text-[11px] font-mono font-medium uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
      {language || "—"}
    </span>
  );
}

export function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 85 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500 font-mono">{pct}%</span>
    </div>
  );
}

const INTENT_LABEL = { support: "support", criticism: "criticism", question: "question", complaint: "complaint", threat: "threat", spam: "spam" };

// Hidden-meaning signals from the deep analysis. Quiet by default: only what adds something to the category.
export function InsightChips({ insight }) {
  if (!insight) return null;
  const chip = "whitespace-nowrap text-[11px] font-medium px-2 py-0.5 rounded border";
  const quiet = "bg-transparent text-slate-500 border-slate-200";
  const warn = "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <>
      {insight.intent === "threat" && <span data-testid="chip-threat" className={`${chip} bg-red-50 text-red-600 border-red-200`}>threat</span>}
      {INTENT_LABEL[insight.intent] && insight.intent !== "threat" && insight.intent !== "spam" && <span className={`${chip} ${quiet}`}>{INTENT_LABEL[insight.intent]}</span>}
      {insight.emotion && insight.emotion !== "neutral" && <span className={`${chip} ${quiet}`}>{insight.emotion}</span>}
      {(insight.sarcasm || 0) >= 0.6 && <span data-testid="chip-sarcasm" className={`${chip} ${warn}`} title="Sounds like praise but reads as mocking">sarcasm</span>}
      {(insight.coordination || 0) >= 0.5 && <span data-testid="chip-coordinated" className={`${chip} ${warn}`} title="Looks part of a coordinated push">coordinated</span>}
      {(insight.coded_language || []).slice(0, 2).map((t) => (
        <span key={t} className={`${chip} ${warn}`} title="Coded wording">“{t}”</span>
      ))}
    </>
  );
}
